/**
 * OwnerShieldVault.js — Module 4 · Owner Shield Vault (Tier 1 only)
 * Route: /owner-vault
 *
 * Renders true asset condition + deferred maintenance + NOI/ROI leakage + procurement ledger.
 * Bypasses regional + PM per doctrine privacy protocol.
 */
import React, { useState } from "react";
import OwnerExecutiveBrief from "./components/OwnerExecutiveBrief";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const shell = { minHeight: "100dvh", background: NAVY, color: INK, padding: "clamp(16px,4vw,48px)", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box" };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(16px,3vw,28px)", marginBottom: 20 };
const btn = { background: CYAN, color: NAVY, border: 0, padding: "10px 20px", fontWeight: 700, borderRadius: 8, cursor: "pointer", fontSize: 13, letterSpacing: "0.03em" };
const input = { width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "10px 12px", borderRadius: 6, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" };

export default function OwnerShieldVault() {
  const [address, setAddress] = useState("5960 Siltstone Lane, Unit 932, Fort Worth TX 76137");
  const [dossier, setDossier] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/mandate/owner-vault?property_address=${encodeURIComponent(address)}`, {
        credentials: "include",
      });
      const d = await r.json();
      if (r.status === 401) {
        setError("Owner Shield Vault is Tier-1 authenticated only. Please sign in as a purse holder to view the true condition dossier.");
        setDossier(null);
        return;
      }
      if (!r.ok) throw new Error(d.detail || "Vault access denied");
      setDossier(d);
    } catch (e) {
      setError(e.message); setDossier(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={shell} data-testid="owner-vault-shell">
      <div style={{ ...card, borderColor: `${CYAN}44` }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          MOOVIN FORWARD LLC · OWNER SHIELD VAULT · TIER 1
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(20px, 3vw, 28px)" }}>True asset condition dossier</h1>
        <div style={{ color: CHROME, marginTop: 6, fontSize: 13 }}>
          Bypass regional + PM · strict direct-to-owner. Digital platform composes the full audit,
          NOI leakage, and procurement ledger for the address you own.
        </div>
      </div>

      <div style={card}>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 12, color: CHROME, marginBottom: 6 }}>Property Address</div>
          <input style={input} value={address} onChange={e => setAddress(e.target.value)} data-testid="vault-address-input" />
        </label>
        <div style={{ marginTop: 12 }}>
          <button style={btn} onClick={load} disabled={loading} data-testid="vault-load-btn">
            {loading ? "Composing dossier…" : "Load Owner Vault"}
          </button>
        </div>
        {error && <div style={{ marginTop: 12, color: "#f04040" }} data-testid="vault-error">{error}</div>}
      </div>

      {dossier && (
        <>
          <OwnerExecutiveBrief propertyAddress={address} />
          <div style={card} data-testid="vault-condition">
            <h3 style={{ marginTop: 0, color: CYAN, fontSize: 14, letterSpacing: "0.15em" }}>TRUE ASSET CONDITION</h3>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              <Metric label="Walkthroughs on File" value={dossier.true_asset_condition.walkthroughs_on_file} />
              <Metric label="Life-Safety Open" value={dossier.true_asset_condition.life_safety_open} color="#f04040" />
              <Metric label="Deficiencies Open" value={dossier.true_asset_condition.deficiencies_open} color="#f5a942" />
            </div>
          </div>

          <div style={card} data-testid="vault-leakage">
            <h3 style={{ marginTop: 0, color: CYAN, fontSize: 14, letterSpacing: "0.15em" }}>NOI + ROI LEAKAGE</h3>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              <Metric label="Market Rent Baseline" value={`$${(dossier.noi_roi_leakage.market_rent_baseline || 0).toLocaleString()}`} />
              <Metric label="Stabilized Target Projection" value={`$${(dossier.noi_roi_leakage.stabilized_target_projection || 0).toLocaleString()}`} color={CYAN} />
              <Metric label="Active Payout Records" value={dossier.noi_roi_leakage.active_payouts_count} />
            </div>
          </div>

          <div style={card} data-testid="vault-procurement">
            <h3 style={{ marginTop: 0, color: CYAN, fontSize: 14, letterSpacing: "0.15em" }}>PROCUREMENT LEDGER · LINE ITEMS</h3>
            <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>
              ${dossier.procurement_ledger.total_procurement.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 12, color: CHROME }}>
              {dossier.procurement_ledger.line_items.length} line items on record for this address.
            </div>
          </div>

          <div style={card} data-testid="vault-deferred">
            <h3 style={{ marginTop: 0, color: CYAN, fontSize: 14, letterSpacing: "0.15em" }}>DEFERRED MAINTENANCE AUDIT</h3>
            {dossier.deferred_maintenance_audit.length === 0 ? (
              <div style={{ color: CHROME }}>No deferred maintenance items logged for this address yet.</div>
            ) : (
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {dossier.deferred_maintenance_audit.slice(0, 20).map((d, i) => (
                  <li key={i} style={{ marginBottom: 6, fontSize: 13 }}>
                    <strong>{d.zone}</strong> · {d.code_ref} · <em style={{ color: d.severity === "life_safety" ? "#f04040" : "#f5a942" }}>{d.severity}</em>
                    {d.notes && <span style={{ color: CHROME }}> — {d.notes}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={{ ...card, borderColor: `${CYAN}44` }}>
            <div style={{ fontSize: 11, color: CYAN, letterSpacing: "0.15em" }}>PRIVACY PROTOCOL</div>
            <div style={{ marginTop: 4, fontSize: 13 }}>{dossier.privacy_protocol}</div>
            <div style={{ marginTop: 8, fontSize: 11, color: CHROME }}>Compiled at {dossier.compiled_at}</div>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, color = INK }) {
  return (
    <div style={{ background: "rgba(0,0,0,0.25)", padding: 14, borderRadius: 8, border: `1px solid ${CHROME}22` }}>
      <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}
